import {
  CommunityGuidelinesIcon,
  FAQIcon,
  FavoritesIcon,
  FeedbackIcon,
  HowItWorksIcon,
  MyProfileIcon,
  PrivacyIcon,
  SettingsIcon,
  TermsIcon,
} from 'assets/icons/SideBarMenuIcons/SideBarIcons';
import { useTranslation } from 'react-i18next';
import LogoutIcon from '../../../../../../assets/icons/buttonIcons/LogoutIcon';
import {
  AppMode,
  SideBarSteps,
} from '../../../../../../helpers/constants/enums';
import Avatar from '../../../../atomics/Avatar';
import SideBarCard from '../../SideBarCard';
import ExmindsIndividualActions from './ExmindsIndividualActions';
import * as SC from './styles';
import HeartIcon from '../../../../../../assets/icons/HeartIcon';
import {
  ExmindsBusinessActions,
  ExmindsIndivualActions,
  SideBarUser,
} from '../../../../../../uiModels/interfaces/sidebar';

interface SideBarMainPageStepProps {
  user: SideBarUser;
  avatar: string;
  isProfileImageCompressing: boolean;
  exminderView: boolean;
  shouldAnimate: boolean;
  onHandleChangeRole: CallbackFunction;
  onMyProfileClick: CallbackFunction;
  onAccountSettingsClick: CallbackFunction;
  individualActions?: ExmindsIndivualActions;
  businessActions?: ExmindsBusinessActions;
  onHowItWorksClick: CallbackFunction;
  onCommunityGuidelinesClick: CallbackFunction;
  onFaqClick: CallbackFunction;
  onFeedbackClick: CallbackFunction;
  onTermsAndConditionsClick: CallbackFunction;
  onPrivacyPolicyClick: CallbackFunction;
  onLogout: CallbackFunction;
  currentStep: number;
  isAuthenticated: boolean;
  isOnboardingSkipped: boolean;
  hasReferralAccount: boolean;
  appMode: AppMode;
}

export default function SideBarMainPageStep({
  user,
  avatar,
  isProfileImageCompressing,
  exminderView,
  shouldAnimate,
  individualActions,
  businessActions,
  onHandleChangeRole,
  onMyProfileClick,
  onAccountSettingsClick,
  onHowItWorksClick,
  onCommunityGuidelinesClick,
  onFaqClick,
  onFeedbackClick,
  onTermsAndConditionsClick,
  onPrivacyPolicyClick,
  onLogout,
  currentStep,
  isAuthenticated,
  isOnboardingSkipped,
  hasReferralAccount,
  appMode,
}: SideBarMainPageStepProps) {
  const { t } = useTranslation();

  const isGuestProfile = !isAuthenticated || isOnboardingSkipped;
  const isBusiness = appMode === AppMode.BUSINESS;
  const iconColor = isBusiness ? 'black' : '#0082AA';

  const generateProfileText = (): string => {
    if (!isAuthenticated) {
      return t('auth.logIn');
    }
    if (isOnboardingSkipped) {
      return t('sidebarMenu.continueOnboarding');
    }
    return t(`sidebarMenu.myProfile`);
  };

  const generateExtendedBoldText = (): string => {
    if (isGuestProfile || appMode === AppMode.BUSINESS) {
      return '';
    }
    return exminderView ? t('general.exminder') : t('general.explorer');
  };

  if (currentStep !== SideBarSteps.MainPageStep) {
    return null;
  }

  return (
    <SC.Container>
      <SC.MainContent>
        <SC.MenuItemsContent>
          <SC.UserDetailsContainer>
            <Avatar
              width={125}
              height={125}
              image={avatar}
              isImageCompressing={isProfileImageCompressing}
              switchIcon={!isGuestProfile && appMode === AppMode.INDIVIDUAL}
              isExminderView={exminderView && !isGuestProfile}
              onHandleChangeRole={onHandleChangeRole}
              animate={shouldAnimate}
              isVerified={
                exminderView &&
                !isGuestProfile &&
                appMode === AppMode.INDIVIDUAL
              }
            />
            <SC.NameContainer>
              {isGuestProfile
                ? t(`sidebarMenu.guestProfile`)
                : `${user?.firstName?.toUpperCase()} ${user?.lastName?.toUpperCase()}`}
            </SC.NameContainer>
            {isGuestProfile && (
              <SC.EmailContainer>{user.email}</SC.EmailContainer>
            )}
          </SC.UserDetailsContainer>
          <SC.ProfileAccountSection gap={isBusiness ? 20 : 15}>
            <SideBarCard
              icon={<MyProfileIcon />}
              text={generateProfileText()}
              onClick={onMyProfileClick}
              extendedBoldText={generateExtendedBoldText()}
              iconColor={iconColor}
            />
            {appMode === AppMode.BUSINESS && (
              <>
                <SideBarCard
                  icon={<FavoritesIcon />}
                  text={t(`sidebarMenu.business.favoriteExperiences`)}
                  onClick={() => businessActions?.onFavoriteExperiencesClick()}
                  disabled={isGuestProfile}
                  iconColor={iconColor}
                />
                <SideBarCard
                  icon={<HeartIcon />}
                  text={t(`sidebarMenu.business.favoriteCompanies`)}
                  onClick={() => businessActions?.onFavoriteCompaniesClick()}
                  disabled={isGuestProfile}
                  iconColor={iconColor}
                />
              </>
            )}
            <SideBarCard
              icon={<SettingsIcon />}
              text={t(`sidebarMenu.accountSettings`)}
              onClick={onAccountSettingsClick}
              iconColor={iconColor}
            />
          </SC.ProfileAccountSection>
          {appMode === AppMode.INDIVIDUAL && (
            <SC.InformationSection gap={15}>
              <SC.HorizontalSeparator isBusiness={false} />
              <ExmindsIndividualActions
                actions={{
                  onMyCalendarClick: individualActions?.onMyCalendarClick,
                  onFavoritesClick: individualActions?.onFavoritesClick,
                  onMoneyWithExmindsClick:
                    individualActions?.onMoneyWithExmindsClick,
                  onInvoicesClick: individualActions?.onInvoicesClick,
                  onReferralDashboardClick:
                    individualActions?.onReferralDashboardClick,
                  onMeetingPlacesClick: individualActions?.onMeetingPlacesClick,
                }}
                isAuthenticated={isAuthenticated}
                isGuestProfile={isGuestProfile}
                exminderView={exminderView}
                hasReferralAccount={hasReferralAccount}
              />
            </SC.InformationSection>
          )}
          <SC.HorizontalSeparator isBusiness={isBusiness} />
          <SC.InformationSection gap={isBusiness ? 20 : 15}>
            <SideBarCard
              icon={<HowItWorksIcon />}
              text={t(`sidebarMenu.howItWorks`)}
              onClick={onHowItWorksClick}
              iconColor={iconColor}
            />
            {appMode === AppMode.INDIVIDUAL && (
              <SideBarCard
                icon={<CommunityGuidelinesIcon />}
                text={t(`sidebarMenu.communityGuidelines`)}
                onClick={onCommunityGuidelinesClick}
                iconColor={iconColor}
              />
            )}
            <SideBarCard
              icon={<FAQIcon />}
              text={t(`sidebarMenu.faq`)}
              onClick={onFaqClick}
              iconColor={iconColor}
            />
            <SideBarCard
              icon={<TermsIcon />}
              text={t(`sidebarMenu.termsAndConditions`)}
              onClick={onTermsAndConditionsClick}
              iconColor={iconColor}
            />
            <SideBarCard
              icon={<PrivacyIcon />}
              text={t(`sidebarMenu.privacyPolicy`)}
              onClick={onPrivacyPolicyClick}
              iconColor={iconColor}
            />
            <SideBarCard
              icon={<FeedbackIcon />}
              iconColor="#26C3A7"
              text={t(`sidebarMenu.feedback`)}
              onClick={onFeedbackClick}
              disabled={isGuestProfile}
            />
          </SC.InformationSection>
          <SC.HorizontalSeparator isBusiness={isBusiness} />
        </SC.MenuItemsContent>
      </SC.MainContent>
      <SC.LogoutContainer>
        <SideBarCard
          icon={<LogoutIcon />}
          text={t(`auth.logOut`)}
          onClick={onLogout}
          disabled={!isAuthenticated}
          iconColor={iconColor}
        />
      </SC.LogoutContainer>
    </SC.Container>
  );
}
